import { useFindMany } from "@gadgetinc/react";
import {
  PlusIcon,
  RectangleStackIcon,
  ArrowPathIcon,
} from "@heroicons/react/16/solid";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../api";
import { OverflowFrame } from "../../../externalComponents";
import LoadingSpinner from "../../../externalComponents/LoadingSpinner";
import { PageHeading } from "../../../externalComponents/PageHeading";
import { SettingsSidebar } from "../../../externalComponents/SettingsSidebar";
import { Badge } from "../../../externalComponents/tailwind/badge";
import EmptyState from "../../../externalComponents/tailwind/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../externalComponents/tailwind/table";
import { formatDateTime } from "../../../functions/date-utils";
import { CustomerContext } from "../../../providers";
import { encodeFiltersToUrl } from "../../contacts/utils/filterUrlUtils";

export default function ImportsPage() {
  const { customer } = useContext(CustomerContext);
  const navigate = useNavigate();

  const [{ data, fetching }] = useFindMany(api.csvImport, {
    live: true,
    filter: {
      status: { isSet: true },
      tag: { isSet: true },
    },
    select: {
      id: true,
      status: true,
      createdAt: true,
      processedAt: true,
      rowCount: true,
      successCount: true,
      duplicateCount: true,
      errorCount: true,
      errorLog: true,
      tag: { name: true, id: true },
    },
    sort: {
      createdAt: "Descending",
    },
  });

  const CsvImportList = () => (
    <div className="mx-3 md:mx-0">
      {fetching ? (
        <LoadingSpinner />
      ) : data && data.length > 0 ? (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Creation date</TableHeader>
              <TableHeader>Auto Tag name</TableHeader>
              <TableHeader className="text-center">Rows</TableHeader>
              <TableHeader className="text-center">Duplicates</TableHeader>
              <TableHeader className="text-center">Imported</TableHeader>
              <TableHeader className="text-center">Status</TableHeader>
              <TableHeader className="text-center">Errors</TableHeader>
              <TableHeader className="text-center">Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="w-[10%]">
                  {formatDateTime(row.createdAt, "shortTimeMediumDate")}
                </TableCell>
                <TableCell className="w-[20%]">
                  <Badge>{row.tag?.name || "Tag deleted"}</Badge>
                </TableCell>
                <TableCell className="text-center">
                  {row.rowCount || 0}
                </TableCell>
                <TableCell className="text-center">
                  {row.duplicateCount || 0}
                </TableCell>
                <TableCell className="text-center w-[20%]">
                  {row.successCount || 0} (
                  {(
                    ((row.successCount || 0) /
                      (row.rowCount - (row.duplicateCount || 0))) *
                    100
                  ).toFixed(0)}
                  %)
                </TableCell>
                <TableCell className="text-center">
                  <Badge
                    color={
                      row.status === "success"
                        ? "lime"
                        : row.status === "processing"
                          ? "cyan"
                          : row.status === "pending"
                            ? "zinc"
                            : "rose"
                    }
                  >
                    {row.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-center ">
                  {row.errorLog && (
                    <div className="flex justify-center">
                      <span
                        className="text-red-500 cursor-help"
                        title={row.errorLog}
                      >
                        ⚠️
                      </span>
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex justify-center items-center gap-2">
                    {row.status === "success" && row.tag?.name && (
                      <div className="relative group">
                        <button
                          onClick={() => {
                            // Create a filter for tags containing the auto tag name
                            const filterGroups = [
                              {
                                ors: [
                                  {
                                    type: "tag",
                                    operator: "contains",
                                    operatorLabel: "contains",
                                    requiresValue: true,
                                    value: row.tag.name,
                                    tagId: row.tag.id,
                                  },
                                ],
                              },
                            ];
                            const encodedFilters =
                              encodeFiltersToUrl(filterGroups);
                            navigate(`/contacts?filters=${encodedFilters}`);
                          }}
                          className="p-1 text-gray-600 hover:text-pink transition-colors"
                          aria-label="Create segment"
                        >
                          <RectangleStackIcon className="h-4 w-4" />
                        </button>
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-white text-gray-900 text-sm rounded-lg shadow-lg border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                          Create segment
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
                        </div>
                      </div>
                    )}
                    {["failed", "pending"].includes(row.status) && (
                      <div className="relative group">
                        <button
                          onClick={async () => {
                            await api.csvImport.retry(row.id);
                          }}
                          className="p-1 text-gray-600 hover:text-pink transition-colors"
                          aria-label="Retry import"
                        >
                          <ArrowPathIcon className="h-4 w-4" />
                        </button>
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-white text-gray-900 text-sm rounded-lg shadow-lg border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                          Retry import
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
                        </div>
                      </div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <EmptyState
          heading="You have no CSV imports"
          action={async () => {
            if (customer.isSubscribed) {
              navigate("/contacts/import");
            }
          }}
          icon={<PlusIcon />}
          actionText="Import contacts"
          img="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        />
      )}
    </div>
  );

  return (
    <>
      <SettingsSidebar>
        <PageHeading
          title="Imports"
          primaryAction="New import"
          handlePrimaryAction={() => navigate("/contacts/import")}
        />
        <OverflowFrame>
          <CsvImportList />
        </OverflowFrame>
      </SettingsSidebar>
    </>
  );
}
